// "eCW status codes" — the claim status codes a Cobalt practice's claims carry, with
// their claim counts and what Atlas reads each as (2026-10-21).
//
// CDE Medical Associates arrived with 16 practice-specific eCW codes Cobalt carries no
// description for. Nothing here guesses: a code in neither the practice's own map nor
// the built-in one reads David's default (submitted: it exists in eCW, so it was
// billed) until he enters its meaning from eCW's own status list. Two faces of one
// card:
//
//   Setup > Data (read-only)            which codes still read the default and how
//                                       many claims sit under each, pointing at
//                                       Practice Rules.
//   Practice Details > Practice Rules   the same table with an Atlas-status select per
//   (editable, admins)                  code and a Save button.
//
//   GET /api/practices/<id>/ecw-status-codes    codes, unmapped_count, default_status, atlas_statuses
//   PUT /api/practices/<id>/ecw-status-map      {map: {CODE: status | null}}
//
// Absent for a practice with no Cobalt claims and on a 403. Codes and counts only.

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";

import "./EcwStatusCodesCard.css";

const API_URL = process.env.REACT_APP_API_URL || "";

export function ecwStatusCodesUrl(practiceId) {
  return `${API_URL}/api/practices/${practiceId}/ecw-status-codes`;
}

export function ecwStatusMapUrl(practiceId) {
  return `${API_URL}/api/practices/${practiceId}/ecw-status-map`;
}

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const err = new Error((body && body.error) || fallback);
    err.status = response.status;
    throw err;
  }
  if (!body || typeof body !== "object") {
    throw new Error("The response could not be read. Try again.");
  }
  return body;
}

// "2 codes on 15 claims with no meaning entered (read as submitted)" for the summary.
export function unmappedLine(data) {
  if (!data) return "";
  const codes = data.unmapped_codes || [];
  const claims = Number(data.unmapped_count) || 0;
  if (!codes.length) return "Every eCW status code on this practice's claims has a meaning.";
  return `${codes.length} ${codes.length === 1 ? "code" : "codes"} on ${claims} ${
    claims === 1 ? "claim" : "claims"
  } with no meaning entered (read as ${data.default_status || "submitted"})`;
}

// `embedded` (Practice Details' Billing Settings form, 2026-10-25): a section of that
// form with no Save of its own; the form saves it through the ref (`ref.current.save()`,
// resolves when saved or when nothing changed, throws with the reason).
const EcwStatusCodesCard = forwardRef(function EcwStatusCodesCard({ practiceId, editable = false, embedded = false }, ref) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Pending edits: {CODE: status} not yet saved. "" means "remove the practice's entry".
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    if (!practiceId) return;
    try {
      const body = await request(ecwStatusCodesUrl(practiceId), {}, "Could not load the eCW status codes.");
      setData(body);
      setLoadError(null);
    } catch (err) {
      if (err.status === 403 || err.status === 404) {
        setHidden(true);
        return;
      }
      setLoadError(err.message);
    }
  }, [practiceId]);

  useEffect(() => {
    setData(null);
    setHidden(false);
    setEdits({});
    setMessage(null);
    load();
  }, [load]);

  // Saves the pending edits; true when something was saved. Throws with the reason.
  const persist = async () => {
    const changes = {};
    Object.keys(edits).forEach((code) => {
      changes[code] = edits[code] === "" ? null : edits[code];
    });
    if (!Object.keys(changes).length) return false;
    const body = await request(
      ecwStatusMapUrl(practiceId),
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ map: changes }),
      },
      "Could not save the status meanings."
    );
    setData(body);
    setEdits({});
    return true;
  };

  useImperativeHandle(ref, () => ({ save: persist }));

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setMessage(null);
    try {
      if (await persist()) {
        setMessage({ type: "success", text: "Status meanings saved. Claims take the new meaning on their next Cobalt sync." });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!practiceId || hidden) return null;
  if (!data && !loadError) return null;
  const codes = (data && data.codes) || [];
  // Nothing to say for a practice whose claims do not come through Cobalt.
  if (data && !codes.length && !editable) return null;

  const rows = editable ? codes : codes.filter((row) => row.source === "none");
  const pending = Object.keys(edits).length;

  return (
    <div className={embedded ? "integrations-section ecw-status-codes" : "data-card ecw-status-codes"} data-testid="ecw-status-codes">
      {embedded ? <h3 className="practice-rules-section-title">eCW status codes</h3> : <h2>eCW status codes</h2>}
      <p className="data-note">
        {embedded
          ? "What Atlas reads each eCW claim status code as. Claims take a new meaning on their next Cobalt sync."
          : editable
          ? "What Atlas reads each eCW claim status code as. Codes eCW invented for this practice carry no description in the Cobalt feed, so their meanings are entered here from eCW's own status list, never guessed. Claims take a new meaning on their next Cobalt sync."
          : `A claim whose eCW status code has no meaning entered reads as ${data && data.default_status ? data.default_status : "submitted"} (it exists in eCW, so it was billed). Meanings are set under Practice Details → Practice Rules.`}
      </p>

      {loadError && <p className="data-error-line">{loadError}</p>}
      {message && (
        <p className={message.type === "error" ? "data-error-line" : "ecw-codes-saved"} data-testid="ecw-codes-message">
          {message.text}
          <button type="button" className="message-close-sm" onClick={() => setMessage(null)} aria-label="Dismiss">&times;</button>
        </p>
      )}

      {data && (
        <>
          <p className="ecw-codes-summary" data-testid="ecw-codes-summary">{unmappedLine(data)}</p>
          {rows.length > 0 && (
            <table className="ecw-codes-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>eCW description</th>
                  <th className="num">Claims</th>
                  <th>Atlas status</th>
                  {editable && <th>From</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const current = Object.prototype.hasOwnProperty.call(edits, row.code)
                    ? edits[row.code]
                    : row.source === "practice" ? row.atlas_status : "";
                  return (
                    <tr key={row.code} data-testid={`ecw-code-${row.code}`}>
                      <td><code>{row.code}</code></td>
                      <td className={row.description ? "" : "ecw-codes-muted"}>{row.description || "not carried by Cobalt"}</td>
                      <td className="num">{row.claims}</td>
                      <td>
                        {editable ? (
                          <select
                            aria-label={`Atlas status for ${row.code}`}
                            value={current}
                            onChange={(e) => setEdits((prev) => ({ ...prev, [row.code]: e.target.value }))}
                            disabled={saving}
                          >
                            <option value="">
                              {row.builtin_status ? `built-in: ${row.builtin_status}` : `default: ${data.default_status || "submitted"}`}
                            </option>
                            {(data.atlas_statuses || []).map((status) => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        ) : (
                          <span className={`ecw-codes-status ${row.source === "none" ? "is-default" : ""}`}>
                            {row.atlas_status}{row.source === "none" ? " (default)" : ""}
                          </span>
                        )}
                      </td>
                      {editable && (
                        <td className="ecw-codes-muted">
                          {row.source === "practice" ? "this practice" : row.source === "builtin" ? "built-in" : "—"}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {editable && codes.length === 0 && (
            <p className="data-note ecw-codes-muted">No Cobalt claims yet; codes appear after the first sync.</p>
          )}
          {editable && !embedded && (
            <div className="ecw-codes-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={save}
                disabled={saving || !pending || data.migration_pending}
                data-testid="ecw-codes-save"
              >
                {saving ? "Saving…" : pending ? `Save ${pending} ${pending === 1 ? "meaning" : "meanings"}` : "Save"}
              </button>
              {data.migration_pending && (
                <span className="data-error-line">The Cobalt settings column is not in the database yet.</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default EcwStatusCodesCard;
